/**
 * Servicio de Almacenamiento Local
 * Monedero Digital NFC
 * 
 * Maneja localStorage con encriptación y manejo de errores
 */

import encryptionService from './encryptionService';
import { STORAGE_KEYS, ERROR_TYPES } from '../utils/constants';

class StorageService {
  /**
   * Guarda datos en localStorage (con encriptación opcional)
   */
  set(key, value, encrypt = false) {
    try {
      let dataToStore = value;

      if (encrypt) {
        dataToStore = encryptionService.encrypt(value);
      } else if (typeof value !== 'string') {
        dataToStore = JSON.stringify(value);
      }

      localStorage.setItem(key, dataToStore);
      return true;
    } catch (error) {
      console.error(`Error guardando en localStorage (${key}):`, error);
      
      if (error.name === 'QuotaExceededError') {
        throw new Error(ERROR_TYPES.STORAGE_FULL);
      }
      
      return false;
    }
  }

  /**
   * Obtiene datos de localStorage (con desencriptación opcional)
   */
  get(key, decrypt = false) {
    try {
      const data = localStorage.getItem(key);
      
      if (!data) {
        return null;
      }

      if (decrypt) {
        return encryptionService.decrypt(data);
      }

      // Intenta parsear como JSON
      try {
        return JSON.parse(data);
      } catch {
        return data; // Si no es JSON, retorna string
      }
    } catch (error) {
      console.error(`Error obteniendo de localStorage (${key}):`, error);
      return null;
    }
  }

  /**
   * Elimina un elemento de localStorage
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error eliminando de localStorage (${key}):`, error);
      return false;
    }
  }

  /**
   * Limpia todo el localStorage
   */
  clear() {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error limpiando localStorage:', error);
      return false;
    }
  }

  /**
   * Verifica si existe una clave
   */
  has(key) {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Obtiene el tamaño usado del localStorage
   */
  getSize() {
    let total = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        total += localStorage[key].length + key.length;
      }
    }
    return total;
  }

  /**
   * Guarda la sesión del usuario
   */
  saveSession(userData) {
    const sessionData = {
      ...userData,
      loginTime: Date.now(),
      expiresAt: Date.now() + (30 * 60 * 1000) // 30 minutos
    };
    
    return this.set(STORAGE_KEYS.USER_SESSION, sessionData, true);
  }

  /**
   * Obtiene la sesión del usuario
   */
  getSession() {
    const session = this.get(STORAGE_KEYS.USER_SESSION, true);
    
    if (!session) {
      return null;
    }

    // Verifica si la sesión expiró
    if (Date.now() > session.expiresAt) {
      this.clearSession();
      return null;
    }

    return session;
  }

  /**
   * Actualiza el tiempo de expiración de la sesión
   */
  refreshSession() {
    const session = this.getSession();
    
    if (session) {
      session.expiresAt = Date.now() + (30 * 60 * 1000);
      return this.set(STORAGE_KEYS.USER_SESSION, session, true);
    }
    
    return false;
  }

  /**
   * Limpia la sesión del usuario
   */
  clearSession() {
    return this.remove(STORAGE_KEYS.USER_SESSION);
  }

  /**
   * Guarda transacciones
   */
  saveTransactions(transactions) {
    return this.set(STORAGE_KEYS.TRANSACTIONS, transactions, true);
  }

  /**
   * Obtiene transacciones
   */
  getTransactions() {
    return this.get(STORAGE_KEYS.TRANSACTIONS, true) || [];
  }

  /**
   * Agrega una nueva transacción
   */
  addTransaction(transaction) {
    const transactions = this.getTransactions();
    transactions.unshift(transaction); // Agrega al inicio
    return this.saveTransactions(transactions);
  }

  /**
   * Guarda productos
   */
  saveProducts(products) {
    return this.set(STORAGE_KEYS.PRODUCTS, products);
  }

  /**
   * Obtiene productos
   */
  getProducts() {
    return this.get(STORAGE_KEYS.PRODUCTS) || [];
  }

  /**
   * Guarda estudiantes
   */
  saveStudents(students) {
    return this.set(STORAGE_KEYS.STUDENTS, students, true);
  }

  /**
   * Obtiene estudiantes
   */
  getStudents() {
    return this.get(STORAGE_KEYS.STUDENTS, true) || [];
  }

  /**
   * Busca un estudiante por NFC ID
   */
  findStudentByNFC(nfcId) {
    const students = this.getStudents();
    return students.find(s => s.nfcId === nfcId);
  }

  /**
   * Actualiza el saldo de un estudiante
   */
  updateStudentBalance(studentId, newBalance) {
    const students = this.getStudents();
    const index = students.findIndex(s => s.id === studentId);
    
    if (index !== -1) {
      students[index].saldoActual = newBalance;
      return this.saveStudents(students);
    }
    
    return false;
  }

  /**
   * Guarda configuración
   */
  saveConfig(config) {
    return this.set(STORAGE_KEYS.CONFIG, config);
  }

  /**
   * Obtiene configuración
   */
  getConfig() {
    return this.get(STORAGE_KEYS.CONFIG);
  }

  /**
   * Crea un backup completo de todos los datos
   */
  createBackup() {
    try {
      const backup = {
        timestamp: new Date().toISOString(),
        version: '1.0.0',
        data: {
          transactions: this.getTransactions(),
          products: this.getProducts(),
          students: this.getStudents(),
          config: this.getConfig()
        }
      };

      const backupKey = `${STORAGE_KEYS.BACKUP}_${Date.now()}`;
      this.set(backupKey, backup, true);

      // Mantiene solo los últimos 5 backups
      this.cleanOldBackups(5);

      return backupKey;
    } catch (error) {
      console.error('Error creando backup:', error);
      return null;
    }
  }

  /**
   * Restaura desde un backup
   */
  restoreBackup(backupKey) {
    try {
      const backup = this.get(backupKey, true);
      
      if (!backup || !backup.data) {
        return false;
      }

      this.saveTransactions(backup.data.transactions || []);
      this.saveProducts(backup.data.products || []);
      this.saveStudents(backup.data.students || []);
      this.saveConfig(backup.data.config || {});

      return true;
    } catch (error) {
      console.error('Error restaurando backup:', error);
      return false;
    }
  }

  /**
   * Limpia backups antiguos, manteniendo solo los N más recientes
   */
  cleanOldBackups(keepCount = 5) {
    try {
      const allKeys = Object.keys(localStorage);
      const backupKeys = allKeys
        .filter(key => key.startsWith(STORAGE_KEYS.BACKUP))
        .sort()
        .reverse();

      // Elimina los backups más antiguos
      backupKeys.slice(keepCount).forEach(key => {
        this.remove(key);
      });

      return true;
    } catch (error) {
      console.error('Error limpiando backups:', error);
      return false;
    }
  }

  /**
   * Obtiene lista de backups disponibles
   */
  getAvailableBackups() {
    try {
      const allKeys = Object.keys(localStorage);
      const backupKeys = allKeys
        .filter(key => key.startsWith(STORAGE_KEYS.BACKUP))
        .sort()
        .reverse();

      return backupKeys.map(key => {
        const backup = this.get(key, true);
        return {
          key,
          timestamp: backup?.timestamp,
          version: backup?.version
        };
      });
    } catch (error) {
      console.error('Error obteniendo backups:', error);
      return [];
    }
  }

  /**
   * Exporta datos como JSON para descarga
   */
  exportData() {
    return {
      exportDate: new Date().toISOString(),
      version: '1.0.0',
      transactions: this.getTransactions(),
      products: this.getProducts(),
      students: this.getStudents(),
      config: this.getConfig()
    };
  }

  /**
   * Importa datos desde JSON
   */
  importData(jsonData) {
    try {
      if (jsonData.transactions) this.saveTransactions(jsonData.transactions);
      if (jsonData.products) this.saveProducts(jsonData.products);
      if (jsonData.students) this.saveStudents(jsonData.students);
      if (jsonData.config) this.saveConfig(jsonData.config);
      
      return true;
    } catch (error) {
      console.error('Error importando datos:', error);
      return false;
    }
  }
}

// Exporta una instancia única (Singleton)
const storageService = new StorageService();
export default storageService;
